<template>
  <global-modal name="event-modal-company-profile" @before-open="beforeOpen">
    <Modal @close="$modal.hide('event-modal-company-profile')" v-if="!!details">
      <form @submit.prevent="onSubmit" class="event-modal-company-profile">
        <h1 class="event-modal-company-profile__title">
          {{ $t("event.companyProfile.title") }}
        </h1>

        <div class="event-modal-company-profile__logo">
          <div class="event-modal-company-profile__logo-image">
            <template>
              <img :src="companyLogo" :alt="details.name_rus" />
            </template>
            <Loader v-if="isUploadingLogo" is-absolute />
          </div>

          <div class="event-modal-company-profile__logo-content">
            <div class="event-modal-company-profile__logo-content-title">
              {{ $t("event.companyProfile.modal.logo") }}
            </div>

            <p class="event-modal-company-profile__logo-content-text">
              {{ $t("event.companyProfile.modal.logoDescription") }}
              <br />
              {{ $t("event.companyProfile.modal.logoSize") }}
              <br />
              {{ $t("event.companyProfile.modal.logoResolution") }}
            </p>
            <div class="event-modal-company-profile__logo-content-file">
              <span>
                <input
                  ref="companyProfileLogo"
                  type="file"
                  class="event-modal-company-profile__logo-content-file-input"
                  @input="logoChanged"
                />

                <LinkIcon
                  :disabled="isUploadingLogo"
                  icon="load"
                  class="event-modal-company-profile__logo-content-file-btn"
                  v-if="hasAccessToWrite"
                >
                  {{ $t("event.companyProfile.modal.logoLoadBtn") }}
                </LinkIcon>
              </span>

              <LinkIcon
                :disabled="isUploadingLogo"
                class="event-modal-company-profile__logo-content-file-btn"
                @click="removeImage"
                v-if="details.image && hasAccessToWrite"
              >
                {{ $t("event.companyProfile.modal.logoRemoveBtn") }}
              </LinkIcon>
            </div>

            <div
              v-if="logoError"
              class="event-modal-company-profile__logo-error"
            >
              {{ $t(logoError) }}
            </div>
          </div>
        </div>

        <div class="event-modal-company-profile__row">
          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("event.companyProfile.modal.companyNameLang1") }} *
              </template>

              <input
                type="text"
                v-model="details.name_rus"
                v-validate="'required'"
                name="companyTitle"
                :disabled="!hasAccessToWrite"
              />

              <template slot="error" v-if="errors.has('companyTitle')">
                {{ errors.first("companyTitle") }}
              </template>
            </InputLabel>
          </div>

          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("event.companyProfile.modal.companyNameLang2") }} *
              </template>

              <input
                type="text"
                v-model="details.name_eng"
                name="companyTitleEnglish"
                v-validate="'required'"
                :disabled="!hasAccessToWrite"
              />

              <template slot="error" v-if="errors.has('companyTitleEnglish')">
                {{ errors.first("companyTitleEnglish") }}
              </template>
            </InputLabel>
          </div>
        </div>

        <div class="event-modal-company-profile__row">
          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("event.companyProfile.modal.country") }} *
              </template>

              <Select
                name="country"
                v-validate="{ required: true, single_dropdown_in: countries }"
                v-model="details.country_id"
                @input="changeIsCountryRussiaSelected"
                :options="countries"
                :disabled="!hasAccessToWrite"
              />

              <template slot="error" v-if="errors.has('country')">
                {{ errors.first("country") }}
              </template>
            </InputLabel>
          </div>

          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("event.companyProfile.modal.companyPhone") }} *
              </template>

              <input
                type="tel"
                v-mask="'+##################'"
                placeholder="+7 900 000 00 00"
                v-validate="'required|phone'"
                v-model="details.phone"
                name="companyPhone"
                :disabled="!hasAccessToWrite"
              />

              <template slot="error" v-if="errors.has('companyPhone')">
                {{ errors.first("companyPhone") }}
              </template>
            </InputLabel>
          </div>
        </div>

        <div class="event-modal-company-profile__row">
          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("event.companyProfile.modal.mailingAddress") }}
              </template>

              <input
                type="text"
                name="mailingAddress"
                v-model="details.mail_address"
                :disabled="!hasAccessToWrite"
              />

              <template slot="error" v-if="errors.has('mailingAddress')">
                {{ errors.first("mailingAddress") }}
              </template>
            </InputLabel>
          </div>

          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("event.companyProfile.modal.addressForm.title") }}
              </template>

              <input
                name="address"
                type="text"
                v-model="details.address"
                @input="details.address = details.address.replace(regex, '')"
                :disabled="!hasAccessToWrite"
              />
            </InputLabel>
          </div>
        </div>

        <div class="event-modal-company-profile__row">
          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("common.companyWebsite") }}
              </template>

              <input
                type="text"
                name="website"
                v-model="details.website"
                v-validate="'website'"
                :disabled="!hasAccessToWrite"
              />

              <template slot="error" v-if="errors.has('website')">
                <div style="display: flex;">
                  <div style="margin-right: 5px">
                    {{ errors.first("website") }}
                  </div>

                  <tooltip
                    clickable
                    v-if="errors.firstByRule('website', 'website')"
                  >
                    <icon name="help-filled" width="16" height="16" />
                    <template #content>
                      <p class="event-modal-company-profile_site-error">
                        {{ $t("event.companyProfile.modal.websiteError") }}
                      </p>
                    </template>
                  </tooltip>
                </div>
              </template>
            </InputLabel>
          </div>

          <div class="event-modal-company-profile__row-col">
            <InputLabel>
              <template #label>
                {{ $t("event.companyProfile.modal.email") }} *
              </template>

              <input
                type="email"
                placeholder="info@example.ru"
                name="email"
                v-model="details.email"
                v-validate="'required'"
                :disabled="!hasAccessToWrite"
              />

              <template slot="error" v-if="errors.has('email')">
                {{ errors.first("email") }}
              </template>
            </InputLabel>
          </div>
        </div>

        <div class="event-modal-company-profile__row" v-if="hasAccessToWrite">
          <div v-if="isSaving">
            <PrimaryButton type="button" :disabled="true">
              {{ $t("event.companyProfile.modal.submitting") }}
            </PrimaryButton>
          </div>

          <div v-else>
            <PrimaryButton type="submit">
              {{ $t("event.companyProfile.modal.submit") }}
            </PrimaryButton>

            <span
              class="text-error"
              v-if="errors.any()"
              style="padding-left: 10px;"
            >
              {{ $t("common.validationError") }}
            </span>
          </div>
        </div>
      </form>
    </Modal>
  </global-modal>
</template>

<script>
import { mapGetters } from "vuex";
import apiCommon from "@/api/common";
import apiParticipant from "@/api/participant";
import { fileLogoValidate } from "@/helpers/common";
import { asianArabicRegex } from "@/helpers/constants/regex";
import noPhoto from "/public/static/images/event-no-company-image.svg";
import {
  PrimaryButton,
  LinkIcon,
  Loader,
  InputLabel,
  Select,
  Tooltip
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import Modal from "./Modal.vue";
import getError from "@/helpers/error";

export default {
  name: "EventModalCompanyProfile",

  components: {
    Loader,
    PrimaryButton,
    Select,
    InputLabel,
    LinkIcon,
    Modal,
    Tooltip
  },

  data() {
    return {
      isSaving: false,
      isUploadingLogo: false,
      logoError: null,
      regex: asianArabicRegex,
      details: {
        address: "",
        country_id: null,
        email: "",
        id: null,
        image: null,
        mail_address: "",
        name_eng: "",
        name_rus: "",
        phone: "",
        website: ""
      },
      isCountryRussiaSelected: false,
      countries: [],
      noPhoto: noPhoto,
      logo: null
    };
  },
  computed: {
    ...mapGetters("event", ["eventId", "company", "hasAccessToWrite"]),

    companyLogo() {
      return this.company.image?.thumbnail_path
        ? `${process.env.VUE_APP_URL}storage/${this.company.image.thumbnail_path}`
        : this.noPhoto;
    }
  },

  methods: {
    onSubmit() {
      if (!this.hasAccessToWrite) {
        return;
      }

      this.$validator.validateAll().then(isValid => {
        if (!isValid) {
          return;
        }

        this.isSaving = true;
        this.sendRequest();
      });
    },

    sendRequest() {
      const data = this.getUpdateData();
      apiParticipant
        .updateCompany(this.eventId, this.company.id, data)
        .then(response => {
          if (response && response.data)
            this.$store.dispatch("event/updateCompany", response.data);
          this.isSaving = false;
          this.$modal.hide("event-modal-company-profile");
        })
        .catch(response => {
          this.isSaving = false;
          console.error(response);
        });
    },

    getUpdateData() {
      const result = this.details;
      delete result.country;
      delete result.id;
      delete result.image;

      return result;
    },

    async beforeOpen() {
      this.isSaving = false;
      this.countries = [];
      this.details = Object.assign({}, this.company);
      if (this.company.country) {
        this.details.country_id = this.company.country.id;

        this.isCountryRussiaSelected =
          this.company.country.name_rus?.toLowerCase() === "россия" ||
          this.company.country.name_eng?.toLowerCase() === "russia";
      }
      apiCommon.getCountryList().then(x => {
        if (x && x.data) {
          this.countries = x.data.reduce((acc, x) => {
            acc.push({
              value: x.id,
              title: i18n.locale === "en" ? x.name_eng : x.name_rus
            });

            return acc;
          }, []);
        }
      });
    },

    logoChanged(files) {
      this.logoError = null;
      this.isUploadingLogo = true;

      if (files && files.target.files[0]) {
        try {
          fileLogoValidate(files.target.files[0]);
        } catch (error) {
          this.logoError = error;
          this.isUploadingLogo = false;
          this.$refs.companyProfileLogo.value = null;
          return;
        }

        const formData = new FormData();
        formData.append("image", files.target.files[0]);
        this.isSaving = true;
        this.$refs.companyProfileLogo.value = null;
        apiParticipant
          .updateLogo(this.eventId, this.company.id, formData)
          .then(response => {
            if (response && response.data) {
              this.details.image = {
                id: response.data.id,
                thumbnail_path: `${process.env.VUE_APP_URL}storage/${response.data.thumbnail_path}`
              };
              this.$store.dispatch("event/updateCompanyLogo", response.data);
              this.isUploadingLogo = false;
              this.isSaving = false;
            }
          })
          .catch(e => {
            this.$store.dispatch("common/setErrorMessage", getError(e));
          })
          .finally(() => {
            this.isUploadingLogo = false;
            this.isSaving = false;
          });
      }
    },

    async removeImage() {
      this.isUploadingLogo = true;
      await apiParticipant.removeLogo(this.eventId, this.company.id);
      this.details.image = null;
      await this.$store.dispatch("event/updateCompanyLogo", null);
      this.isUploadingLogo = false;
    },

    changeIsCountryRussiaSelected(value) {
      const country = this.countries.find(country => country.value === value);
      this.isCountryRussiaSelected =
        country?.title?.toLowerCase() === "россия" ||
        country?.title?.toLowerCase() === "russia";
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-modal-company-profile {
  padding: 55px 170px 64px 80px;

  @include respond-down(lg) {
    padding: 47px 24px;
  }

  @include respond-down(md) {
    padding: 24px 16px 32px 16px;
  }

  &__title {
    @include h1();

    margin-bottom: 41px;

    @include respond-down(lg) {
      margin-bottom: 39px;
    }

    @include respond-down(md) {
      margin-bottom: 32px;
    }
  }

  &__subtitle {
    font-size: 16px;
    font-weight: 500;
    line-height: 1.5;
    color: #505050;
    margin: 0;
  }
  &_site-error {
    @include description();
    font-size: 12px;
    max-width: 300px;
  }
  &__logo {
    display: flex;

    @include respond-down(md) {
      flex-direction: column;
    }

    &-image {
      width: 200px;
      height: 112px;
      background: #f6f6f6;

      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;

      margin-right: 40px;

      @include respond-down(md) {
        margin-bottom: 16px;
      }

      img {
        max-width: 100%;
        max-height: 100%;

        object-fit: cover;
      }
    }

    &-content {
      display: flex;
      flex-direction: column;

      &-title {
        font-size: 16px;
        font-weight: 500;
        line-height: 1.5;

        color: #505050;

        @include respond-down(md) {
          font-size: 14px;
          margin-bottom: 4px;
        }
      }

      &-text {
        @include description();

        width: 415px;

        margin: auto 0;

        @include respond-down(md) {
          width: 100%;
          font-size: 12px;
          margin-bottom: 13px;
        }
      }

      &-file {
        display: flex;
        align-items: center;

        > span {
          position: relative;

          display: inline-block;

          @include respond-down(md) {
            margin-bottom: 32px;
          }
        }

        &-input {
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          font-size: 0;
          cursor: pointer;
        }

        &-btn {
          font-weight: bold;
          margin-right: 15px;

          &.link-icon .svg-icon--load {
            color: #009ee3;
          }
        }
      }
    }

    &-error {
      @include description();
      font-size: 12px;
      color: #d32f2f;
      margin-top: 8px;
    }
  }

  &__row {
    display: flex;

    justify-content: space-between;

    width: 780px;

    margin: 32px 0;

    @include respond-down(lg) {
      width: 100%;
    }

    @include respond-down(md) {
      margin: 0 0 16px 0;
      flex-direction: column;
    }

    &-col {
      width: 380px;

      &:first-child:not(:last-child) {
        margin-right: 20px;

        @include respond-down(md) {
          margin-bottom: 16px;
          margin-right: 0;
        }
      }

      @include respond-down(lg) {
        width: 100%;
        min-width: 350px;
      }

      @include respond-down(md) {
        width: 100%;
        min-width: 288px;
      }

      &.--full-width {
        width: 100%;
      }
    }
  }
}
</style>
